import {
  StateDefinition,
  TypeOfStateDeclaration,
} from '../storage/state-declaration.js';
import { isStateValue, StateValue } from '../storage/state-value.js';
import { CustomError } from '../error/custom-error.js';
import { TypeDescription } from '../typing/type-description.js';
import { ObjectTypeDescription } from '../typing/object-type-description.js';
import { failNever } from '../utils/fail-never.js';
import { isArray } from '../typing/array.js';
import { getDate } from '../typing/date.js';
import { UpsertType } from '../storage/upsert-type.js';
import { PatchType } from '../storage/patch-type.js';
import { Telemetry } from '../tracer/telemetry.js';

export function getStateDataKeys<TState extends StateDefinition<any, any, any>>(
  state: TState,
  val: UpsertType<TState> | PatchType<TState> | TypeOfStateDeclaration<TState>,
): {
  data: Record<string, StateValue>;
  keys: Record<string, StateValue>;
} {
  return {
    data: getStateData(state.type, val),
    keys: getStateKeys(state, val),
  };
}

export function getStateData<TState extends StateDefinition<any, any, any>>(
  description: ObjectTypeDescription<TypeOfStateDeclaration<TState>>,
  val: UpsertType<TState> | PatchType<TState> | TypeOfStateDeclaration<TState>,
): Record<string, StateValue> {
  return iterateData(description, '', val);
}

type IterateDataResult =
  | { success: true; data: Record<string, StateValue> }
  | { success: false; error: string; context?: Record<string, string> };

export function iterateData(
  type: TypeDescription,
  prefix: string,
  val: unknown,
): Record<string, StateValue> {
  const result = iterateDataInternal(type, prefix, val);
  if (!result.success) {
    throw new CustomError(result.error, result.context ?? {});
  }
  return result.data;
}

function iterateDataInternal(
  type: TypeDescription,
  prefix: string,
  val: unknown,
): IterateDataResult {
  if (type.type === 'string') {
    if (typeof val === 'string') {
      return { success: true, data: { [prefix]: val } };
    } else {
      return {
        success: false,
        error: 'unable to map string',
        context: { key: prefix },
      };
    }
  } else if (type.type === 'number') {
    if (typeof val === 'number') {
      return { success: true, data: { [prefix]: val } };
    } else {
      return {
        success: false,
        error: 'unable to map number',
        context: { key: prefix },
      };
    }
  } else if (type.type === 'boolean') {
    if (typeof val === 'boolean') {
      return { success: true, data: { [prefix]: val } };
    } else {
      return {
        success: false,
        error: 'unable to map boolean',
        context: { key: prefix },
      };
    }
  } else if (type.type === 'date') {
    const date = getDate(val);
    if (date) {
      return { success: true, data: { [prefix]: date } };
    } else {
      return {
        success: false,
        error: 'unable to map date',
        context: { key: prefix },
      };
    }
  } else if (type.type === 'numberAsText') {
    if (typeof val === 'string' && !isNaN(parseFloat(val))) {
      return { success: true, data: { [prefix]: val } };
    } else {
      return {
        success: false,
        error: 'unable to map numberAsText',
        context: { key: prefix },
      };
    }
  } else if (type.type === 'literal') {
    if (
      val === type.constant &&
      (typeof val === 'string' ||
        typeof val === 'number' ||
        typeof val === 'boolean')
    ) {
      return { success: true, data: { [prefix]: val } };
    } else {
      return {
        success: false,
        error: 'unable to map literal',
        context: { key: prefix },
      };
    }
  } else if (type.type === 'array') {
    if (!isArray(val)) {
      return {
        success: false,
        error: 'unable to map array',
        context: { key: prefix },
      };
    }

    const items = Object.values(val);
    let data: Record<string, StateValue> = {};
    for (let i = 0; i < items.length; i++) {
      const itemResult = iterateDataInternal(
        type.itemType,
        `${prefix}.${i}`,
        items[i],
      );
      if (!itemResult.success) {
        return {
          success: false,
          error: 'unable to map array item',
          context: { key: prefix, index: i.toString() },
        };
      }
      data = { ...data, ...itemResult.data };
    }
    return { success: true, data };
  } else if (type.type === 'object') {
    if (val === null || val === undefined || typeof val !== 'object') {
      return {
        success: false,
        error: 'unable to map empty object',
        context: { key: prefix },
      };
    }

    let data: Record<string, StateValue> = {};
    const props: string[] = [];
    for (const [key, value] of Object.entries(val)) {
      const propType = type.object[key];
      props.push(key);
      if (propType) {
        const propResult = iterateDataInternal(
          propType,
          prefix.length > 0 ? `${prefix}.${key}` : key,
          value,
        );
        if (!propResult.success) {
          return {
            success: false,
            error: 'unable to map object property',
            context: { key: prefix, propertyName: key },
          };
        }
        data = { ...data, ...propResult.data };
      }
    }

    const typeProps = Object.entries(type.object);
    const missedTypeProps = typeProps.filter(
      ([name]) => props.indexOf(name) === -1,
    );
    for (const [name, missedProp] of missedTypeProps) {
      const propResult = iterateDataInternal(
        missedProp,
        prefix.length > 0 ? `${prefix}.${name}` : name,
        name in val ? (val as any)[name] : undefined,
      );
      if (!propResult.success) {
        return {
          success: false,
          error: 'unable to map object property',
          context: { key: prefix, propertyName: name },
        };
      }
      data = { ...data, ...propResult.data };
    }

    return { success: true, data };
  } else if (type.type === 'optional') {
    const result = iterateDataInternal(type.optionalType, prefix, val);
    if (!result.success) {
      return { success: true, data: {} };
    }
    return result;
  } else if (type.type === 'nullable') {
    const result = iterateDataInternal(type.nullableType, prefix, val);
    if (!result.success) {
      return { success: true, data: {} };
    }
    return result;
  } else if (type.type === 'union') {
    for (const unionType of type.unionTypes) {
      const result = iterateDataInternal(unionType, prefix, val);
      if (result.success) {
        return result;
      }
    }
    return {
      success: false,
      error: 'unable to map union',
      context: { key: prefix },
    };
  } else if (type.type === 'record') {
    if (val === null || val === undefined || typeof val !== 'object') {
      return {
        success: false,
        error: 'unable to map empty record',
        context: { key: prefix },
      };
    }

    let data: Record<string, StateValue> = {};
    for (const [key, value] of Object.entries(val)) {
      const recordResult = iterateDataInternal(
        type.valueType,
        prefix.length > 0
          ? `${prefix}."${key.replaceAll('"', '\\"')}"`
          : `"${key.replaceAll('"', '\\"')}"`,
        value,
      );
      if (!recordResult.success) {
        return recordResult;
      }
      data = { ...data, ...recordResult.data };
    }

    return { success: true, data };
  } else if (type.type === 'func' || type.type === 'promise') {
    return {
      success: false,
      error: 'unparsable type',
      context: { type: type.type },
    };
  } else {
    failNever(type, 'unexpected type');
  }
}

export function getPartialStateKeys<
  TState extends StateDefinition<any, any, any>,
>(
  state: TState,
  val: UpsertType<TState> | PatchType<TState> | TypeOfStateDeclaration<TState>,
): Record<string, StateValue> {
  const keys: Record<string, StateValue> = {};

  for (const key of state.key) {
    const value = key in val ? (val as any)[key] : null;
    if (isStateValue(value)) {
      keys[key] = value;
    }
  }

  return keys;
}
export function getStateKeys<TState extends StateDefinition<any, any, any>>(
  state: TState,
  val: UpsertType<TState> | PatchType<TState> | TypeOfStateDeclaration<TState>,
): Record<string, StateValue> {
  const keys: Record<string, StateValue> = {};

  for (const key of state.key) {
    const value = key in val ? (val as any)[key] : null;
    if (isStateValue(value)) {
      keys[key] = value;
    } else {
      throw new CustomError('invalid key value', { key });
    }
  }

  return keys;
}

export function deserializeData<T>(
  description: TypeDescription,
  data: Record<string, StateValue>,
  telemetry: Telemetry,
): T {
  const tracer = telemetry.createTracer('deserialize');
  return tracer.createSpan(
    'deserialize',
    { attributes: { type: description.type } },
    () => {
      const entries = Object.entries(data)
        .map(([key, value]) => ({ key, value }))
        .sort((a, b) => (a.key > b.key ? 1 : -1));
      return iterateMap(description, '', entries);
    },
  );
}

export function mapToState<TState extends StateDefinition<any, any, any>>(
  state: TState,
  data: Record<string, StateValue>,
  telemetry: Telemetry,
): TypeOfStateDeclaration<TState> {
  return deserializeData(state.type, data, telemetry);
}

type Entry = { key: string; value: StateValue };

type IterateMapResult =
  | { success: true; data: any }
  | { success: false; error: string; context?: Record<string, string> };

function getValuesWithPrefix(prefix: string, entries: Entry[]): Entry[] {
  if (entries.length === 0) {
    return [];
  }
  const start = findFirstPrefix(entries, prefix);
  if (start === -1) {
    return [];
  }
  const end = findFirstNonPrefix(entries, prefix, start);
  return entries.slice(start, end);
}

function findFirstPrefix(entries: Entry[], prefix: string): number {
  let low = 0;
  let high = entries.length - 1;
  let result = -1;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    if (entries[mid]!.key.startsWith(prefix)) {
      result = mid;
      high = mid - 1;
    } else if (entries[mid]!.key < prefix) {
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  return result;
}

function findFirstNonPrefix(
  entries: Entry[],
  prefix: string,
  start: number,
): number {
  let low = start;
  let high = entries.length - 1;
  let result = entries.length;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    if (!entries[mid]!.key.startsWith(prefix)) {
      result = mid;
      high = mid - 1;
    } else {
      low = mid + 1;
    }
  }
  return result;
}

function getValue(entries: Entry[], key: string): StateValue | undefined {
  let low = 0;
  let high = entries.length - 1;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    if (entries[mid]!.key === key) {
      return entries[mid]!.value;
    } else if (entries[mid]!.key < key) {
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  return undefined;
}

function iterateMap(
  type: TypeDescription,
  prefix: string,
  entries: Entry[],
): any {
  const result = iterateMapInternal(type, prefix, entries);
  if (!result.success) {
    throw new CustomError(result.error, result.context ?? {});
  }
  return result.data;
}

function iterateMapInternal(
  type: TypeDescription,
  prefix: string,
  entries: Entry[],
): IterateMapResult {
  if (type.type === 'string') {
    const val = getValue(entries, prefix);
    if (typeof val === 'string') {
      return { success: true, data: val };
    } else {
      return {
        success: false,
        error: 'unable to map string',
        context: { key: prefix },
      };
    }
  } else if (type.type === 'number') {
    const val = getValue(entries, prefix);
    if (typeof val === 'number') {
      return { success: true, data: val };
    } else {
      return {
        success: false,
        error: 'unable to map number',
        context: { key: prefix },
      };
    }
  } else if (type.type === 'boolean') {
    const val = getValue(entries, prefix);
    if (typeof val === 'boolean') {
      return { success: true, data: val };
    } else {
      return {
        success: false,
        error: 'unable to map boolean',
        context: { key: prefix },
      };
    }
  } else if (type.type === 'date') {
    const val = getValue(entries, prefix);
    const date = getDate(val);
    if (date) {
      return { success: true, data: date };
    } else {
      return {
        success: false,
        error: 'unable to map date',
        context: { key: prefix },
      };
    }
  } else if (type.type === 'numberAsText') {
    const val = getValue(entries, prefix);
    if (typeof val === 'string' && !isNaN(parseFloat(val))) {
      return { success: true, data: val };
    } else {
      return {
        success: false,
        error: 'unable to map numberAsText',
        context: { key: prefix },
      };
    }
  } else if (type.type === 'literal') {
    const val = getValue(entries, prefix);
    if (
      val === type.constant &&
      (typeof val === 'string' ||
        typeof val === 'number' ||
        typeof val === 'boolean')
    ) {
      return { success: true, data: val };
    } else {
      return {
        success: false,
        error: 'unable to map literal',
        context: { key: prefix },
      };
    }
  } else if (type.type === 'array') {
    const result: any[] = [];
    let i = 0;

    while (true) {
      const key = getPrefixedKey(prefix, `${i}`);
      const indexEntries = getValuesWithPrefix(key, entries);
      if (indexEntries.length > 0) {
        const itemResult = iterateMapInternal(type.itemType, key, indexEntries);
        if (!itemResult.success) {
          return itemResult;
        }
        result.push(itemResult.data);
      } else {
        break;
      }
      i++;
    }
    return { success: true, data: result };
  } else if (type.type === 'object') {
    const result: any = {};

    for (const [key, propType] of Object.entries(type.object)) {
      const propKey = getPrefixedKey(prefix, key);
      const propValues = getValuesWithPrefix(propKey, entries);
      const propResult = iterateMapInternal(propType, propKey, propValues);
      if (!propResult.success) {
        return propResult;
      }
      if (propResult.data !== undefined) {
        result[key] = propResult.data;
      }
    }

    return { success: true, data: result };
  } else if (type.type === 'nullable') {
    if (entries.length === 0) {
      return { success: true, data: null };
    } else {
      return iterateMapInternal(type.nullableType, prefix, entries);
    }
  } else if (type.type === 'optional') {
    if (entries.length === 0) {
      return { success: true, data: undefined };
    } else {
      return iterateMapInternal(type.optionalType, prefix, entries);
    }
  } else if (type.type === 'union') {
    if (type.discriminator) {
      const discriminatorKey = getPrefixedKey(prefix, type.discriminator.key);
      const rawDiscriminator = getValue(entries, discriminatorKey);
      const discriminatorValue =
        rawDiscriminator === undefined ? 'undefined' : String(rawDiscriminator);
      const discriminatorType = type.discriminator.mappings[discriminatorValue];
      if (discriminatorType) {
        return iterateMapInternal(discriminatorType, prefix, entries);
      } else {
        for (const unionType of type.discriminator.unmapped) {
          const result = iterateMapInternal(unionType, prefix, entries);
          if (result.success) {
            return result;
          }
        }
      }
    } else {
      for (const unionType of type.unionTypes) {
        const result = iterateMapInternal(unionType, prefix, entries);
        if (result.success) {
          return result;
        }
      }
    }

    return {
      success: false,
      error: 'unparsable union',
      context: { unionName: type.name },
    };
  } else if (type.type === 'record') {
    const result: any = {};

    const recordPrefix = prefix.length === 0 ? '' : `${prefix}.`;
    const recordEntries =
      prefix.length === 0
        ? entries
        : getValuesWithPrefix(recordPrefix, entries);

    let i = 0;
    while (i < recordEntries.length) {
      const entry = recordEntries[i]!;
      const keyWithoutPrefix = entry.key.substring(recordPrefix.length);
      const stopKeyIndex = keyWithoutPrefix.match(/[^\\]"/)?.index ?? 0;
      const recordKeyQuoted = keyWithoutPrefix.substring(0, stopKeyIndex + 2);
      const recordKey = recordKeyQuoted.substring(
        1,
        recordKeyQuoted.length - 1,
      );
      const escapedRecordKey = recordKey.replaceAll('\\"', '"');

      const fullRecordKey = `${recordPrefix}${recordKeyQuoted}`;

      const start = i;
      let end = i + 1;
      while (
        end < recordEntries.length &&
        recordEntries[end]!.key.startsWith(fullRecordKey)
      ) {
        end++;
      }

      const subEntries = recordEntries.slice(start, end);

      const recordResult = iterateMapInternal(
        type.valueType,
        fullRecordKey,
        subEntries,
      );
      if (!recordResult.success) {
        return recordResult;
      }
      result[escapedRecordKey] = recordResult.data;

      i = end;
    }
    return { success: true, data: result };
  } else if (type.type === 'func' || type.type === 'promise') {
    return {
      success: false,
      error: 'unparsable type',
      context: { type: type.type },
    };
  } else {
    failNever(type, 'unexpected type');
  }
}

function getPrefixedKey(prefix: string, key: string) {
  if (prefix.length === 0) {
    return key;
  } else {
    return `${prefix}.${key}`;
  }
}
